import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import {
  KNOWLEDGE_DOC_FILTER_CHANGE,
  KNOWLEDGE_DOC_OPTIONS_CHANGE,
  useGeckoAnalytics,
} from 'features/analytics'
import { useDocumentTabDataContext } from 'features/knowledgeDocumentViewer/contexts/DocumentTabDataContext'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import {
  FilterOption,
  TooltipOption,
  viewLayoutSchema,
} from 'features/knowledgeDocumentViewer/types'
import { shouldForceHideDocumentBoxes } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { Settings } from 'lucide-react'

const LINK_HIGHLIGHT_KEYS = ['asset', 'docReference'] as const

export function SettingsMenu() {
  const { trackEvent } = useGeckoAnalytics()
  const {
    tooltipOptions,
    setTooltipOptions,
    linkHighlights,
    setLinkHighlights,
    filterOptions,
    setFilterOptions,
    viewLayout,
    setViewLayout,
    isGeckoUser,
  } = useKnowledgeDocumentUXContext()
  const { totalPages } = useDocumentTabDataContext()
  const { showDocumentOcrBoxes } = useKnowledgeDocumentDataContext()
  const forceHideDocumentBoxes =
    shouldForceHideDocumentBoxes(showDocumentOcrBoxes)

  const autoOpenNewTab = tooltipOptions.includes(TooltipOption.AutoOpenNewTab)
  const showTooltips = tooltipOptions.includes(TooltipOption.ShowTooltips)
  const showOcrBoxes =
    !forceHideDocumentBoxes && filterOptions !== FilterOption.RemoveBoxes
  const isMultiPage = totalPages > 1

  const handleViewLayoutChange = (value: string) => {
    const result = viewLayoutSchema.safeParse(value)
    if (!result.success) return
    const newLayout = result.data
    if (newLayout === viewLayout) return

    setViewLayout(newLayout)

    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (newLayout === 'twoPage') {
      const newOptions = [...tooltipOptions]
      if (!newOptions.includes(TooltipOption.ShowMultiplePages)) {
        newOptions.push(TooltipOption.ShowMultiplePages)
        added.push(TooltipOption.ShowMultiplePages)
      }
      if (!newOptions.includes(TooltipOption.MultiPageTurner)) {
        newOptions.push(TooltipOption.MultiPageTurner)
        added.push(TooltipOption.MultiPageTurner)
      }
      setTooltipOptions(newOptions)
      trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
        options: newOptions,
        added_options: added,
        removed_options: removed,
      })
    } else if (viewLayout === 'twoPage') {
      if (tooltipOptions.includes(TooltipOption.ShowMultiplePages)) {
        removed.push(TooltipOption.ShowMultiplePages)
      }
      if (tooltipOptions.includes(TooltipOption.MultiPageTurner)) {
        removed.push(TooltipOption.MultiPageTurner)
      }
      const newOptions = tooltipOptions.filter(
        (opt) =>
          opt !== TooltipOption.ShowMultiplePages &&
          opt !== TooltipOption.MultiPageTurner
      )
      setTooltipOptions(newOptions)
      trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
        options: newOptions,
        added_options: added,
        removed_options: removed,
      })
    }
  }

  const handleAutoOpenNewTabChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(
      TooltipOption.AutoOpenNewTab
    )

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.AutoOpenNewTab]
      added.push(TooltipOption.AutoOpenNewTab)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.AutoOpenNewTab
      )
      removed.push(TooltipOption.AutoOpenNewTab)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleShowTooltipsChange = (checked: boolean) => {
    const currentlyEnabled = tooltipOptions.includes(TooltipOption.ShowTooltips)

    if (checked === currentlyEnabled) {
      return
    }

    let newOptions: TooltipOption[]
    const added: TooltipOption[] = []
    const removed: TooltipOption[] = []

    if (checked) {
      newOptions = [...tooltipOptions, TooltipOption.ShowTooltips]
      added.push(TooltipOption.ShowTooltips)
    } else {
      newOptions = tooltipOptions.filter(
        (opt) => opt !== TooltipOption.ShowTooltips
      )
      removed.push(TooltipOption.ShowTooltips)
    }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: newOptions,
      added_options: added,
      removed_options: removed,
    })
    setTooltipOptions(newOptions)
  }

  const handleLinkHighlightChange = (
    key: keyof typeof linkHighlights,
    checked: boolean
  ) => {
    if (checked === linkHighlights[key]) {
      return
    }

    const newHighlights = { ...linkHighlights, [key]: checked }

    trackEvent(KNOWLEDGE_DOC_OPTIONS_CHANGE.title, {
      options: LINK_HIGHLIGHT_KEYS.filter((k) => newHighlights[k]),
      added_options: checked ? [key] : [],
      removed_options: checked ? [] : [key],
    })
    setLinkHighlights(newHighlights)
  }

  const handleOcrBoxesChange = (checked: boolean) => {
    if (forceHideDocumentBoxes) {
      return
    }

    const currentlyShowingBoxes = filterOptions !== FilterOption.RemoveBoxes

    if (checked === currentlyShowingBoxes) {
      return
    }

    const newFilter = checked
      ? FilterOption.ShowConfirmed
      : FilterOption.RemoveBoxes

    trackEvent(KNOWLEDGE_DOC_FILTER_CHANGE.title, {
      filter_option: newFilter,
      previous_filter: filterOptions,
    })
    setFilterOptions(newFilter)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='inputRaised' size='icon-lg' title='Settings'>
          <Settings size={18} strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='start'>
        <DropdownMenuCheckboxItem
          checked={showTooltips}
          onCheckedChange={handleShowTooltipsChange}>
          Show tooltip when hovering on links
        </DropdownMenuCheckboxItem>
        <DropdownMenuCheckboxItem
          checked={autoOpenNewTab}
          onCheckedChange={handleAutoOpenNewTabChange}>
          Always open links in new tab
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {isGeckoUser && (
          <>
            <DropdownMenuLabel>Link highlights</DropdownMenuLabel>
            <DropdownMenuCheckboxItem
              checked={linkHighlights.asset}
              onCheckedChange={(checked) =>
                handleLinkHighlightChange('asset', checked)
              }>
              Asset highlights
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={linkHighlights.docReference}
              onCheckedChange={(checked) =>
                handleLinkHighlightChange('docReference', checked)
              }>
              Document references
            </DropdownMenuCheckboxItem>
          </>
        )}
        <DropdownMenuCheckboxItem
          checked={showOcrBoxes}
          disabled={forceHideDocumentBoxes}
          onCheckedChange={handleOcrBoxesChange}>
          OCR boxes
        </DropdownMenuCheckboxItem>
        {isMultiPage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Page layout</DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={viewLayout}
              onValueChange={handleViewLayoutChange}>
              <DropdownMenuRadioItem value='continuous'>
                Continuous scroll
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value='single'>
                Single page
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value='twoPage'>
                Two-page spread
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
